// Print filter swatches. The Print filter (filter.p.m.custom.prints) is plain text, so Shopify
// can't auto-populate filter_value.swatch. We read each print's swatch from the matching product's
// Color option (or its shopify.color-pattern metafield) via the Storefront API and paint it client
// side, surviving AJAX filter re-renders.
//
// TT 21 SEP - SEARCH PAGES NOW WORK. The loader always received the search terms on the search
// page, but every lookup below only knew how to ask for a collection by handle. With no handle,
// ensureColors() returned nothing, so on search the only swatches that showed were the few with
// an image in Files (painted by Liquid). The same Soie Paradis swatch appeared on Shorts and was
// blank on a search for "corbin short". Each lookup now asks the Storefront API's search for the
// same terms when there is no collection handle.

const API_VERSION = '2025-10';
const PRINT_METAFIELD = { namespace: 'custom', key: 'prints' };

const _state = {
  token: null,
  handle: null,        // collection handle (collection pages)
  query: null,         // search terms (search pages)
  colors: null,        // [{ name, bg }], fetched once per scope
  colorsPromise: null, // in-flight fetch shared by concurrent callers
  overrides: {},        // print name -> bg, resolved per-print via the prints metafield filter
  fallbackDone: false,
  diagnosed: false
};

const apiUrl = () => `${window.location.origin}/api/${API_VERSION}/graphql.json`;

function graphql(token, query, variables) {
  return fetch(apiUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
    body: JSON.stringify({ query, variables })
  }).then((res) => res.json());
}

const SWATCH_FRAGMENT = `
  options {
    name
    optionValues {
      name
      swatch { image { previewImage { url(transform: { maxWidth: 72 }) } } color }
    }
  }
`;

// Standard taxonomy color metafield. Combined-listing children have no Color option but carry this,
// referencing a color-pattern metaobject that holds the swatch. May be a single ref or a list.
const COLOR_PATTERN_FRAGMENT = `
  colorPattern: metafield(namespace: "shopify", key: "color-pattern") {
    reference { ...moFields }
    references(first: 5) { nodes { ...moFields } }
  }
`;

const METAOBJECT_FIELDS_FRAGMENT = `
  fragment moFields on Metaobject {
    fields {
      key
      value
      reference { ... on MediaImage { image { url(transform: { maxWidth: 72 }) } } }
      references(first: 5) { nodes { ... on MediaImage { image { url(transform: { maxWidth: 72 }) } } } }
    }
  }
`;

function colorOptionOf(product) {
  return product.options.find((o) => {
    const n = o.name.toLowerCase();
    return n === 'color' || n === 'colour';
  });
}

function bgOf(swatch) {
  if (swatch?.image?.previewImage?.url) return `url(${swatch.image.previewImage.url})`;
  if (swatch?.color) return `linear-gradient(to right, ${swatch.color}, ${swatch.color})`;
  return null;
}

// A near-white solid swatch is invisible on the white panel, so it needs a border.
function isNearWhite(bg) {
  const m = bg.match(/#([0-9a-fA-F]{6})/);
  if (!m) return false;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 240;
}

function fieldImageUrl(field) {
  if (field.reference?.image?.url) return field.reference.image.url;
  for (const node of field.references?.nodes || []) {
    if (node?.image?.url) return node.image.url;
  }
  return null;
}

// Prefer a swatch image on the color-pattern metaobject; fall back to a color hex.
function bgFromColorPattern(metafield) {
  if (!metafield) return null;
  const metaobjects = [];
  if (metafield.reference) metaobjects.push(metafield.reference);
  if (metafield.references?.nodes) metaobjects.push(...metafield.references.nodes);

  for (const mo of metaobjects) {
    for (const field of mo?.fields || []) {
      const url = fieldImageUrl(field);
      if (url) return `url(${url})`;
    }
  }
  for (const mo of metaobjects) {
    for (const field of mo?.fields || []) {
      const hex = (field.value || '').match(/#[0-9a-fA-F]{3,8}/);
      if (hex) return `linear-gradient(to right, ${hex[0]}, ${hex[0]})`;
    }
  }
  return null;
}

// The product list for the current scope: a collection by handle, or a storefront search for the
// same terms the search page ran. `fields` is the product selection; `extra` is appended after the
// query (fragments). Returns { nodes, pageInfo } in both cases.
function scopedProductsQuery(scope, args, fields, extra) {
  if (scope.handle) {
    return {
      query: `
        query ScopedProducts($handle: String!, $cursor: String, $filters: [ProductFilter!]) {
          collection(handle: $handle) {
            products(${args}, after: $cursor, filters: $filters) {
              pageInfo { hasNextPage endCursor }
              nodes { ${fields} }
            }
          }
        }
        ${extra || ''}
      `,
      pick: (json) => json.data?.collection?.products || null,
      vars: { handle: scope.handle }
    };
  }
  return {
    query: `
      query ScopedSearch($q: String!, $cursor: String, $filters: [ProductFilter!]) {
        search(query: $q, types: [PRODUCT], ${args}, after: $cursor, productFilters: $filters) {
          pageInfo { hasNextPage endCursor }
          nodes { ... on Product { ${fields} } }
        }
      }
      ${extra || ''}
    `,
    pick: (json) => json.data?.search || null,
    vars: { q: scope.query }
  };
}

// Every distinct Color option value (with a swatch) across the scope. Decoupled from the prints
// on screen so the cache is complete even on a filtered first view.
async function fetchColors(token, scope) {
  const colors = [];
  const seen = new Set();
  let cursor = null;
  let hasNextPage = true;
  const q = scopedProductsQuery(scope, 'first: 250', SWATCH_FRAGMENT);

  while (hasNextPage) {
    const json = await graphql(token, q.query, { ...q.vars, cursor, filters: null });
    if (json.errors) console.warn('[PrintSwatches] color fetch errors:', json.errors);
    const page = q.pick(json);
    if (!page) break;

    for (const product of page.nodes) {
      if (!product || !product.options) continue;
      const colorOption = colorOptionOf(product);
      if (!colorOption) continue;
      for (const ov of colorOption.optionValues) {
        if (seen.has(ov.name)) continue;
        const bg = bgOf(ov.swatch);
        if (bg) {
          colors.push({ name: ov.name, bg });
          seen.add(ov.name);
        }
      }
    }

    hasNextPage = page.pageInfo.hasNextPage;
    cursor = page.pageInfo.endCursor;
  }

  return colors;
}

// For a print name-matching can't resolve, query the scope filtered by that exact print metafield
// value and take a swatch from a matching product (its Color option, else its color-pattern).
async function fetchSwatchForPrint(token, scope, printName) {
  const q = scopedProductsQuery(scope, 'first: 10', `title ${SWATCH_FRAGMENT} ${COLOR_PATTERN_FRAGMENT}`, METAOBJECT_FIELDS_FRAGMENT);
  const filters = [{ productMetafield: { ...PRINT_METAFIELD, value: printName } }];
  try {
    const json = await graphql(token, q.query, { ...q.vars, cursor: null, filters });
    if (json.errors) console.warn(`[PrintSwatches] "${printName}" query errors:`, json.errors);
    const nodes = (q.pick(json)?.nodes || []).filter((p) => p && p.options);
    const printLower = printName.toLowerCase();
    let firstBg = null;
    let firstColorPattern = null;
    for (const product of nodes) {
      const colorOption = colorOptionOf(product);
      if (colorOption) {
        for (const ov of colorOption.optionValues) {
          const bg = bgOf(ov.swatch);
          if (!bg) continue;
          if (ov.name.toLowerCase().includes(printLower)) return bg;
          if (!firstBg) firstBg = bg;
        }
      }
      if (!firstColorPattern) firstColorPattern = bgFromColorPattern(product.colorPattern);
    }

    const result = firstBg || firstColorPattern;
    if (!result) {
      const detail = nodes.map((p) => {
        const co = colorOptionOf(p);
        return {
          title: p.title,
          options: p.options.map((o) => o.name),
          colorOption: co ? co.name : '(none)',
          values: co ? co.optionValues.map((ov) => ({ name: ov.name, swatch: ov.swatch || null })) : [],
          colorPattern: p.colorPattern || null
        };
      });
      console.log(`[PrintSwatches] "${printName}": ${nodes.length} product(s): ${JSON.stringify(detail)}`);
    }
    return result;
  } catch (e) {
    return null;
  }
}

function currentScope() {
  return { handle: _state.handle, query: _state.query };
}

function ensureColors() {
  if (_state.colors) return Promise.resolve(_state.colors);
  if (_state.colorsPromise) return _state.colorsPromise;
  const { token, handle, query } = _state;
  if (!token || (!handle && !query)) return Promise.resolve(null);
  _state.colorsPromise = fetchColors(token, currentScope()).then((colors) => {
    _state.colors = colors;
    return colors;
  });
  return _state.colorsPromise;
}

const norm = (s) => s.toLowerCase().replace(/\s+/g, ' ').trim();
const words = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);

// Match a print to a color: exact, then bidirectional substring, then word-subset.
function bgForPrint(printName, colors) {
  if (_state.overrides[printName]) return _state.overrides[printName];
  const p = norm(printName);
  const pWords = words(printName);
  let partial = null;
  let subset = null;
  for (const c of colors) {
    const n = norm(c.name);
    if (n === p) return c.bg;
    if (!partial && (n.includes(p) || p.includes(n))) partial = c.bg;
    if (!subset && pWords.length) {
      const cWords = words(c.name);
      const [small, big] = pWords.length <= cWords.length ? [pWords, cWords] : [cWords, pWords];
      if (small.length && small.every((w) => big.includes(w))) subset = c.bg;
    }
  }
  return partial || subset;
}

// The collection template renders the facets snippet twice (drawer + sidebar), so paint across all.
function allSwatchEls() {
  return [...document.querySelectorAll('[data-prints-filter] .color-swatch[data-tooltip]')];
}

async function applyPrintSwatches() {
  if (!allSwatchEls().length) return;

  const colors = await ensureColors();
  if (!colors || !colors.length) {
    if (!_state.diagnosed) {
      _state.diagnosed = true;
      console.log(`[PrintSwatches] ${_state.handle || _state.query || ''}: 0 colors fetched`);
    }
    return;
  }

  const unmatched = [];
  const unmatchedSeen = new Set();
  allSwatchEls().forEach((el) => {
    const name = el.dataset.tooltip;
    if (!name || name.toLowerCase() === 'null') return;
    // Already resolved server-side from a Files image (Liquid set a url background) — leave it.
    const existing = el.style.getPropertyValue('--swatch-background');
    if (existing && existing.indexOf('url(') !== -1) return;
    const bg = bgForPrint(name, colors);
    if (bg) {
      el.style.setProperty('--swatch-background', bg);
      if (isNearWhite(bg)) el.classList.add('border');
    } else if (!unmatchedSeen.has(name)) {
      unmatchedSeen.add(name);
      // Use the input's raw value: the display label may be title-cased vs the stored metafield value.
      const input = el.getAttribute('for') ? document.getElementById(el.getAttribute('for')) : null;
      unmatched.push({ name, value: (input && input.value) || name });
    }
  });

  if (unmatched.length && !_state.fallbackDone) {
    _state.fallbackDone = true;
    await Promise.all(unmatched.map(async ({ name, value }) => {
      _state.overrides[name] = await fetchSwatchForPrint(_state.token, currentScope(), value);
    }));
    return applyPrintSwatches();
  }

  if (!_state.diagnosed) {
    _state.diagnosed = true;
    const names = [...new Set(allSwatchEls().map((el) => el.dataset.tooltip).filter((n) => n && n.toLowerCase() !== 'null'))];
    const unresolved = unmatched.map((u) => u.name);
    console.log(
      `[PrintSwatches] ${_state.handle || _state.query || ''}: painted ${names.length - unresolved.length}/${names.length}` +
      (unresolved.length ? ` — unresolved: ${JSON.stringify(unresolved)}` : '')
    );
  }
}

if (!customElements.get('print-swatch-loader')) {
  customElements.define('print-swatch-loader', class extends HTMLElement {
    connectedCallback() {
      const token = this.dataset.token || null;
      const handle = this.dataset.handle || null;
      const query = this.dataset.query || null;
      // Reset the cache if the scope changed (soft-navigation between collections).
      if (handle !== _state.handle || query !== _state.query) {
        _state.colors = null;
        _state.colorsPromise = null;
        _state.overrides = {};
        _state.fallbackDone = false;
        _state.diagnosed = false;
      }
      if (token) _state.token = token;
      _state.handle = handle;
      _state.query = query;
      applyPrintSwatches();
    }
  });
}

// theme:loading:end fires just before the theme swaps the section on AJAX filter updates; rAF defers
// our repaint until the new swatch DOM is in place. Registered once on documentElement.
document.documentElement.addEventListener('theme:loading:end', () => {
  requestAnimationFrame(() => applyPrintSwatches());
});